import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { usePaymentCheckOut } from 'Views/Components/_HumanWritten/Payment/PaymentCheckoutProvider';
import PaymentTypeWrapper from 'Views/Components/_HumanWritten/Payment/PaymentTypeWrapper';
import { WizardStepHeader } from '../Shared/WizardStepHeader';
import { newAlertModal } from 'Views/Components/Modal/NewAlertModal';
import { getPaymentModalContents, getPaymentModalTitle } from './PaymentTabModalUtils';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import { isNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import { useSetupPayment, SetupPaymentError } from 'Hooks/useSetupPayment';
import { PaymentError } from 'Views/Components/_HumanWritten/Payment/usePayment';
import { resetReservationTimer } from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';
import { onWizardSuccess } from 'Util/_HumanWritten/BookingWizard/BookingWizardUtils';
import {
	getEventBookingTransactionIdFromStorage,
	getFerryBookingTransactionIdFromStorage,
} from 'Services/Api/_HumanWritten/BookingService/BookingService';
import { RefundMethod } from 'Models/AppConfigStore';
import { showAlterationRefundModal } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/Modals/AlterationRefundModal';
import { getLastPaymentAttempt } from './PaymentTabUtils';
import { AWAITING_PAYMENT_TIMEOUT } from 'Views/Pages/AwaitEwayPaymentPage';
import { TripType } from '../../BookingWizardTypes';
import { trackAddPaymentInfo } from 'Util/_HumanWritten/Analytics/analyticsEvents';
import { paymentTotalQueryOptions } from 'Views/Components/_HumanWritten/Payment/usePaymentAmount';
import { funnelEventAlreadyTracked, markFunnelEventTracked } from 'Util/_HumanWritten/Analytics/funnelDedupe';
import { previousCardsQueryOptions } from 'Services/Api/_HumanWritten/PaymentService';
import { store } from 'Models/Store';

export interface StripePaymentTabProps {
	paymentCompletedUrl: string;
	timeoutExpiryUrl: string;
	nameToPrefill: string;
	errorCode: string | null;
	onPageLoaded: () => void;
	returnTrip: boolean;
	userId?: string;
	isAlteration: boolean;
	tripType?: TripType;
	bookingToEditId?: string;
	eventBooking: boolean;
}

function PaymentTab({
	paymentCompletedUrl,
	timeoutExpiryUrl,
	errorCode,
	onPageLoaded,
	returnTrip,
	userId,
	isAlteration,
	tripType,
	bookingToEditId,
	eventBooking,
}: StripePaymentTabProps) {
	const navigate = useNavigate();
	const hasShownErrorModalRef = React.useRef(false);
	const initializedRef = React.useRef(false);
	const [timerResetComplete, setTimerResetComplete] = useState(false);
	// Gates setup-payment until the refund method has been confirmed (alterations) or skipped.
	const [refundFlowResolved, setRefundFlowResolved] = useState(false);
	const [selectedRefundMethod, setSelectedRefundMethod] = useState<RefundMethod | undefined>(undefined);
	const refundModalHandledRef = React.useRef(false);

	const { initializePaymentFlow, setTransactionDetails } = usePaymentCheckOut();
	const queryClient = useQueryClient();

	const storedTransactionId = eventBooking
		? getEventBookingTransactionIdFromStorage()
		: getFerryBookingTransactionIdFromStorage();

	// Staff are offered the customer's saved cards, and Stripe takes most of a second to list them.
	// Fetch the list now, alongside extend-reservation and setup-payment, rather than once the payment
	// form appears after them. Same condition as PaymentTypeWrapper's `canPayWithPreviousCards`, which in
	// the wizard is just "staff" — the wizard never sets `canPayByCardOverride`.
	useEffect(() => {
		if (storedTransactionId && store.isStaff && stringNotEmpty(userId)) {
			// staleTime 0 forces a fresh list: a card saved by the previous payment must show here.
			queryClient.prefetchQuery({ ...previousCardsQueryOptions(userId), staleTime: 0 });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userId]);

	// Before setup-payment runs (it performs any refund server-side), confirm the refund method
	// with the user. The modal lists the methods available to them and lets them pick when more
	// than one applies; it self-skips when no refund is due or credit is the only option. The
	// chosen method is sent to setup-payment and applied. Non-alterations proceed straight
	// through.
	useEffect(() => {
		if (!timerResetComplete || !storedTransactionId || refundModalHandledRef.current) {
			return;
		}
		refundModalHandledRef.current = true;

		if (!isAlteration) {
			setRefundFlowResolved(true);
			return;
		}

		showAlterationRefundModal({
			transactionId: storedTransactionId,
			onConfirm: method => {
				setSelectedRefundMethod(method ?? undefined);
				setRefundFlowResolved(true);
			},
			onCancel: () => {
				navigate(timeoutExpiryUrl, { replace: true });
			},
		}).catch(() => {
			// If refund info can't be fetched, proceed anyway — the backend still resolves the
			// method from the user's role + the tenant default.
			setRefundFlowResolved(true);
		});
	}, [timerResetComplete, storedTransactionId, isAlteration, navigate, timeoutExpiryUrl]);

	// Check for accidental refresh (recent payment attempt)
	useEffect(() => {
		if (!errorCode && storedTransactionId) {
			const lastPaymentAttempt = getLastPaymentAttempt();
			if (lastPaymentAttempt) {
				const now = new Date();
				const cutoffTime = new Date(now.getTime() - AWAITING_PAYMENT_TIMEOUT);
				if (lastPaymentAttempt >= cutoffTime) {
					navigate('/awaiting-payment', { replace: true });
				}
			}
		}
	}, [errorCode, storedTransactionId]);

	// Reset reservation timer on error code
	useEffect(() => {
		if (errorCode !== null && storedTransactionId !== null) {
			resetReservationTimer(storedTransactionId).catch(() => {});
		}
	}, [errorCode, storedTransactionId]);

	// Initialize the payment flow store for booking wizard — once only
	useEffect(() => {
		if (storedTransactionId && !initializedRef.current) {
			initializedRef.current = true;

			initializePaymentFlow({
				transactionId: storedTransactionId,
				userId,
				isAlteration,
				tripType,
				eventBooking,
				paymentLocation: 'booking-wizard',
				redirectUrl: paymentCompletedUrl,
			});

			// Reset reservation timer BEFORE setupPayment is allowed to fire. Both
			// endpoints mutate the same transaction row, and running them concurrently
			// has been observed to surface as a CompleteBooking failure inside
			// CreateTransaction. That failure is caught by
			// CompleteTransactionNoPaymentRequired's catch-all, which marks the
			// transaction FAILED and the alterations EXPIRED_RESERVATION before
			// returning a 409 — destroying the booking. Sequencing the calls keeps
			// the row writes ordered.
			resetReservationTimer(storedTransactionId)
				.catch(() => {})
				.finally(() => setTimerResetComplete(true));
		}
	}, [storedTransactionId]);

	// Call setupPayment via the idempotent hook. Pass null until the reservation
	// timer reset has finished so the two requests can't race.
	const {
		transactionDetails: fetchedTransactionDetails,
		error: setupError,
		isLoading: setupLoading,
		noPaymentRedirectUrl,
	} = useSetupPayment({
		transactionId: timerResetComplete && refundFlowResolved ? storedTransactionId : null,
		redirectUrl: paymentCompletedUrl,
		isEventBooking: eventBooking,
		inCheckIn: false,
		selectedRefundMethod,
	});

	// Update store with transaction details when they arrive
	useEffect(() => {
		if (fetchedTransactionDetails) {
			setTransactionDetails(fetchedTransactionDetails);
		}
	}, [fetchedTransactionDetails, setTransactionDetails]);

	// Handle no-payment-required redirect
	useEffect(() => {
		if (setupError === SetupPaymentError.NO_PAYMENT_REQUIRED && storedTransactionId) {
			onWizardSuccess(navigate, storedTransactionId, isAlteration, returnTrip, eventBooking, userId, false);
		}
	}, [setupError, storedTransactionId]);

	// Map SetupPaymentError to legacy PaymentError for modal display
	const accessCodeError = React.useMemo(() => {
		switch (setupError) {
			case SetupPaymentError.NONE:
				return PaymentError.NO_ERROR;
			case SetupPaymentError.TIMEOUT:
				return PaymentError.TIMEOUT;
			case SetupPaymentError.NO_PAYMENT_REQUIRED:
				return PaymentError.NO_PAYMENT_ERROR;
			case SetupPaymentError.GENERIC:
				return PaymentError.ERROR;
			default:
				return PaymentError.NO_ERROR;
		}
	}, [setupError]);

	// Handle error modal display (only show once)
	useEffect(() => {
		if (
			accessCodeError !== PaymentError.NO_ERROR &&
			accessCodeError !== PaymentError.NO_PAYMENT_ERROR &&
			!hasShownErrorModalRef.current
		) {
			hasShownErrorModalRef.current = true;

			newAlertModal(getPaymentModalTitle(accessCodeError), getPaymentModalContents(accessCodeError), {
				cancelText: 'Return to Payment',
				shouldCloseOnEsc: false,
				shouldCloseOnOverlayClick: false,
			}).then(() => {
				navigate(timeoutExpiryUrl, { replace: true });
			});
		}
	}, [accessCodeError, timeoutExpiryUrl, bookingToEditId, isAlteration, eventBooking, navigate]);

	// Spinner until: init effect has run, timer reset has finished, setup has
	// answered, and we're not displaying an error / handling a no-payment redirect.
	const paymentFormReady =
		initializedRef.current &&
		timerResetComplete &&
		refundFlowResolved &&
		!setupLoading &&
		accessCodeError === PaymentError.NO_ERROR;

	// Called from an effect, not during render: it sets the wizard's state, and a parent's state may not
	// be set while a child renders.
	useEffect(() => {
		if (paymentFormReady && !isNullOrUndefined(storedTransactionId)) {
			onPageLoaded();
		}
	}, [paymentFormReady, storedTransactionId, onPageLoaded]);

	// Fire `add_payment_info` once the authoritative payment total has been fetched, so `value`
	// matches the amount the customer is about to pay (the pricing cache could be stale/incomplete).
	// It reads the same query the payment form shows its amount from, started in the same commit, so
	// the two share one request — which is why it waits for setup-payment, as the form does.
	//
	// The ref covers a repeat within this mount; `funnelDedupe` covers going back to the cart and
	// returning, which remounts this tab and resets the ref.
	const paymentInfoTrackedRef = React.useRef(false);
	useEffect(() => {
		if (!paymentFormReady || paymentInfoTrackedRef.current || !storedTransactionId) {
			return undefined;
		}
		if (funnelEventAlreadyTracked('add_payment_info')) {
			return undefined;
		}
		let cancelled = false;
		queryClient
			.fetchQuery(paymentTotalQueryOptions(eventBooking, false, storedTransactionId))
			.then(prices => {
				if (cancelled || paymentInfoTrackedRef.current) {
					return;
				}
				paymentInfoTrackedRef.current = true;
				markFunnelEventTracked('add_payment_info');
				trackAddPaymentInfo({
					transaction_id: storedTransactionId ?? undefined,
					value: prices?.totalPrice,
				});
			})
			.catch(() => {
				/* total-price fetch failed — skip the analytics value rather than guess */
			});
		return () => {
			cancelled = true;
		};
	}, [paymentFormReady, storedTransactionId, eventBooking, queryClient]);

	if (isNullOrUndefined(storedTransactionId)) {
		return <></>;
	}

	if (!paymentFormReady) {
		return <LottieSpinner />;
	}

	return (
		<div className="booking-wizard-tab payment-step-content">
			<WizardStepHeader
				iconName="credit_card"
				title={<FormattedMessage id="booking-wizard__payment__header" defaultMessage="Payment details" />}
				subtitle={
					<FormattedMessage
						id="booking-wizard__payment__subtitle"
						defaultMessage="Choose how you'd like to pay."
					/>
				}
				showCloseLink={isAlteration}
				bookingToEditId={bookingToEditId}
			/>
			<PaymentTypeWrapper />
		</div>
	);
}

export default PaymentTab;
